// Admin B2B Leads panel — view + progress incoming Geschäftskunden-Anfragen
// through the pipeline: new → contacted → qualified → won / lost.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { Building2, Mail, Phone, Filter, ExternalLink, Check, Clock3, MessageCircle } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800 border-blue-300",
  contacted: "bg-amber-100 text-amber-800 border-amber-300",
  qualified: "bg-purple-100 text-purple-800 border-purple-300",
  won: "bg-emerald-100 text-emerald-800 border-emerald-300",
  lost: "bg-neutral-200 text-neutral-600 border-neutral-300",
};

const VOLUME_LABELS = {
  under_10: "< 10/mo",
  "10_25": "10–25/mo",
  "25_100": "25–100/mo",
  "100_plus": "100+/mo",
};

export default function AdminB2BLeads() {
  const { t } = useLang();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const l = t.admin_leads || {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/b2b/leads");
      setLeads(r.data || []);
    } catch { toast.error("Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/b2b/leads/${id}?status=${status}`);
      toast.success(l.status_updated || "OK");
      load();
    } catch { toast.error("Error"); }
  };

  const filtered = filter === "all" ? leads : leads.filter(x => x.status === filter);

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter(x => x.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-b2b-leads">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-1 flex items-center gap-2">
            <Building2 size={14} /> {l.eyebrow || "B2B Pipeline"}
          </p>
          <h2 className="text-2xl font-black tracking-tight">{l.title || "Geschäftskunden-Anfragen"}</h2>
          <p className="text-sm text-neutral-500 mt-1">{l.subtitle || "Alle eingehenden Business-Leads verwalten."}</p>
        </div>
        <div className="text-3xl font-black tracking-tighter">
          <span className="text-[#FF5A00]">{leads.length}</span>
          <span className="text-xs uppercase tracking-widest text-neutral-400 ms-2 font-bold">{l.total || "Gesamt"}</span>
        </div>
      </div>

      {/* Pipeline summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <PipelineCard
          onClick={() => setFilter("all")}
          active={filter === "all"}
          label={l.all || "Alle"}
          count={leads.length}
        />
        {STATUSES.map(s => (
          <PipelineCard
            key={s}
            onClick={() => setFilter(s)}
            active={filter === s}
            label={l[`status_${s}`] || s}
            count={counts[s] || 0}
            color={STATUS_COLORS[s]}
          />
        ))}
      </div>

      {loading ? (
        <div className="text-neutral-500 py-12 text-center">{t.common.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-neutral-200 py-16 text-center text-neutral-400">
          <Filter size={24} className="mx-auto text-neutral-300 mb-3" />
          {l.empty || "Noch keine Anfragen in diesem Filter."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(lead => (
            <LeadRow
              key={lead.lead_id}
              lead={lead}
              onSetStatus={(s) => setStatus(lead.lead_id, s)}
              t={l}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineCard({ label, count, active, onClick, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-start p-3 border transition-all ${
        active
          ? "border-[#0A0B0D] bg-[#0A0B0D] text-white"
          : "border-neutral-200 bg-white hover:border-[#FF5A00]"
      }`}
      data-testid={`b2b-lead-filter-${label.toLowerCase()}`}
    >
      <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${active ? "text-neutral-400" : "text-neutral-500"}`}>
        {label}
      </div>
      <div className="flex items-center gap-2">
        <div className="text-xl font-black tabular-nums">{count}</div>
        {color && !active && <span className={`inline-block w-2 h-2 rounded-full ${color.split(" ")[0]}`} />}
      </div>
    </button>
  );
}

function LeadRow({ lead, onSetStatus, t }) {
  const [open, setOpen] = useState(false);
  const created = lead.created_at ? new Date(lead.created_at) : null;
  return (
    <div
      className="bg-white border border-neutral-200 hover:border-neutral-300 transition-colors"
      data-testid={`b2b-lead-row-${lead.lead_id}`}
    >
      <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-black tracking-tight truncate">{lead.company}</h3>
            <span className={`text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>
              {t[`status_${lead.status}`] || lead.status}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF5A00]/10 text-[#FF5A00] border border-[#FF5A00]/30 px-2 py-0.5">
              {VOLUME_LABELS[lead.volume] || lead.volume}
            </span>
          </div>
          <div className="text-sm text-neutral-700 mt-1">
            {lead.contact_name} · {lead.interest || "—"}
          </div>
          <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-[#FF5A00]">
              <Mail size={12} /> {lead.email}
            </a>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-[#FF5A00]">
                <Phone size={12} /> {lead.phone}
              </a>
            )}
            {created && (
              <span className="inline-flex items-center gap-1">
                <Clock3 size={12} /> {created.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center flex-shrink-0">
          {lead.status === "new" && (
            <button type="button" onClick={() => onSetStatus("contacted")}
              className="text-xs font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-600 text-white px-3 py-2"
              data-testid={`b2b-lead-btn-contacted-${lead.lead_id}`}
            >
              {t.mark_contacted || "Kontaktiert"}
            </button>
          )}
          {lead.status === "contacted" && (
            <button type="button" onClick={() => onSetStatus("qualified")}
              className="text-xs font-bold uppercase tracking-wider bg-purple-600 hover:bg-purple-700 text-white px-3 py-2"
              data-testid={`b2b-lead-btn-qualified-${lead.lead_id}`}
            >
              {t.mark_qualified || "Qualifiziert"}
            </button>
          )}
          {(lead.status === "qualified" || lead.status === "contacted") && (
            <button type="button" onClick={() => onSetStatus("won")}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2"
              data-testid={`b2b-lead-btn-won-${lead.lead_id}`}
            >
              <Check size={12} /> {t.mark_won || "Gewonnen"}
            </button>
          )}
          {lead.status !== "lost" && lead.status !== "won" && (
            <button type="button" onClick={() => onSetStatus("lost")}
              className="text-xs font-bold uppercase tracking-wider bg-white hover:bg-neutral-100 border border-neutral-300 text-neutral-600 px-3 py-2"
              data-testid={`b2b-lead-btn-lost-${lead.lead_id}`}
            >
              {t.mark_lost || "Verloren"}
            </button>
          )}
          <button type="button" onClick={() => setOpen(o => !o)}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-neutral-100 hover:bg-neutral-200 border border-neutral-200 text-neutral-700 px-3 py-2"
            data-testid={`b2b-lead-btn-toggle-${lead.lead_id}`}
          >
            <ExternalLink size={12} /> {open ? (t.hide || "Zu") : (t.details || "Details")}
          </button>
        </div>
      </div>
      {open && lead.message && (
        <div className="border-t border-neutral-100 bg-neutral-50 p-4 sm:p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1.5">
            <MessageCircle size={12} /> {t.message_label || "Nachricht"}
          </div>
          <p className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">{lead.message}</p>
        </div>
      )}
    </div>
  );
}
